import * as R from 'ramda';
import type { SectionsContext, SectionsData, SectionData, SectionsState } from './types';

export const storeKey = '@portfolio/common/sections2';

export const getSectionsWidth = (sections: SectionsData) =>
	sections.reduce((width, section) => width + section.width, 0);

export const isResizable = (section: SectionData) =>
	!section.isFixed && section.width && section.width > section.minWidth;

export const scaleLeftSections = (delta: number, sections: SectionsData): SectionsData => {
	if (sections.length === 0) return sections;
	let remainingDelta = delta;
	// Reverse sections to change size of the rightmost ones first.
	const result = R.clone(sections).reverse();
	// Treat the rightmost section on the left from separator as the dragged one and ignore its
	// isFixed flag.
	for (const section of result) {
		if (section.width + remainingDelta < section.minWidth) {
			// Section can accomodate only part of the remaining delta.
			// Because this condition could happen only when sections are shrinking,
			// and delta is negative, it is "reduced" by adding positive width difference.
			remainingDelta += section.width - section.minWidth;
			section.width = section.minWidth;
		} else {
			// Section can absord the rest of the remainingDelta.
			section.width += remainingDelta;
			break;
		}
	}
	return result.reverse();
};

export const scaleRightSections = (
	delta: number,
	sections: SectionsData,
	keepTotalWidth: boolean,
): SectionsData => {
	let remainingDelta = delta;
	const result = R.clone(sections);

	// First pass: try to change width of resizable sections only.
	for (const section of result) {
		if (isResizable(section)) {
			if (section.width - remainingDelta < section.minWidth) {
				// Section can accomodate only part of the remaining delta.
				remainingDelta -= section.width - section.minWidth;
				section.width = section.minWidth;
			} else {
				// Section can absorb the rest of the delta.
				section.width -= remainingDelta;
				remainingDelta = 0;
				break;
			}
		}
	}
	// Second pass: try to keep total width by any price except going below minWidth.
	if (keepTotalWidth && remainingDelta !== 0) {
		for (const section of result) {
			if (section.width - remainingDelta < section.minWidth) {
				// Section can accomodate only part of the remaining delta.
				remainingDelta -= section.width - section.minWidth;
				section.width = section.minWidth;
			} else {
				// Section can absord the rest of the delta.
				section.width -= remainingDelta;
				remainingDelta = 0;
				break;
			}
		}
	}
	// It may happen that remainingDelta is still not 0 by this point.
	// We don't have a good solution for this case yet.
	return result;
};

export const getExpansionDelta = (
	{ id, width, initialWidth }: SectionData,
	sections: SectionsState,
) => {
	if (sections.preDragWidths && sections.preDragWidths[id]) {
		return sections.preDragWidths[id] - width;
	}
	return initialWidth - width;
};

export const getAvailableDelta = (
	{ index, parent, sectionsData }: SectionsContext,
	direction: number,
	isFitParent: boolean,
): number => {
	if (!isFitParent) {
		// No restriction on available delta
		return Infinity;
	}
	// Treat the rightmost section on the left from separator as the dragged one and ignore its isFixed flag.
	const draggedSection = sectionsData[index];
	const sections = direction < 0 ? sectionsData.slice(0, index + 1) : sectionsData.slice(index + 1);
	const availableDelta = sections.reduce(
		(delta, section) =>
			delta +
			(!section.isFixed || section === draggedSection ? section.width - section.minWidth : 0),
		0,
	);
	const parentAvailableDelta = parent ? getAvailableDelta(parent, direction, isFitParent) : 0;
	return direction * availableDelta + parentAvailableDelta;
};

export const getDeltaFromRightFlexiSection = (sections: SectionsData): number => {
	const section = sections
		.slice()
		.reverse()
		.find(({ isFixed }) => !isFixed);

	return section ? section.width - section.minWidth : 0;
};
